import {
  createTheme,
  defaultVariantColorsResolver,
  rem,
  type CSSVariablesResolver,
  type MantineColorsTuple,
  type VariantColorsResolver,
} from '@mantine/core';

/**
 * Aroo theme. Source of truth: docs/design/aroo-handoff (Claude Design handoff, 2026-10-08).
 * Dark-first; the shell stays black in both schemes. Yellow is the accent and the one
 * primary action per screen; it is never used as text on white.
 */

// Spartan yellow scale (0–9); 5 is brand #FFD400.
const spartan: MantineColorsTuple = [
  '#FFFBE0',
  '#FFF4B3',
  '#FFEC80',
  '#FFE44D',
  '#FFDC26',
  '#FFD400',
  '#E6BF00',
  '#B39400',
  '#6B5600',
  '#3D3100',
];

// Neutral scale used by Mantine's dark scheme: 7 = surface, 8 = body.
const dark: MantineColorsTuple = [
  '#F5F5F2',
  '#C9C9CC',
  '#A3A3A8',
  '#6E6E74',
  '#3A3A3F',
  '#2C2C30',
  '#1D1D20',
  '#141416',
  '#0A0A0B',
  '#050506',
];

// Semantic scales. Dark scheme uses index 5, light uses index 7 (see primaryShade below).
const success: MantineColorsTuple = [
  '#E8FBF1',
  '#C6F3DC',
  '#9BE9C2',
  '#6EDFA8',
  '#52DA98',
  '#3DD68C',
  '#2AB574',
  '#16803F',
  '#0F5E2E',
  '#0A4020',
];
const warning: MantineColorsTuple = [
  '#FFF3E6',
  '#FFE0C2',
  '#FFCB97',
  '#FFB56B',
  '#FFA854',
  '#FF9F43',
  '#E6842A',
  '#B45309',
  '#8A3F06',
  '#612C04',
];
const danger: MantineColorsTuple = [
  '#FFECEC',
  '#FFD1D1',
  '#FFAFAF',
  '#FF8D8D',
  '#FF7A7A',
  '#FF6B6B',
  '#E84F4F',
  '#C62828',
  '#9A1E1E',
  '#6E1515',
];
const info: MantineColorsTuple = [
  '#EAF3FF',
  '#CCE2FF',
  '#A8CDFF',
  '#86BBFF',
  '#75B5FF',
  '#6CB2FF',
  '#3F8FE6',
  '#1D5FC4',
  '#164A99',
  '#0F3570',
];

/**
 * Colour per tone as scheme-aware tokens (the `--a-*` variables below), so a `color` prop gives
 * the handoff's shade in both schemes: yellow fills stay #FFD400 with black text, but yellow is
 * never text on white (light uses #6B5600 for accent text, borders and links).
 */
const TONES: Record<
  string,
  { fill: string; hover: string; text: string; soft: string; on: string }
> = {
  spartan: {
    fill: 'var(--a-accent)',
    hover: '#FFE44D',
    text: 'var(--a-accent-text)',
    soft: 'var(--a-accent-soft)',
    on: '#0A0A0B',
  },
  danger: {
    fill: 'var(--a-danger)',
    hover: 'var(--a-danger-hover)',
    text: 'var(--a-danger)',
    soft: 'var(--a-danger-soft)',
    on: 'var(--a-on-tone)',
  },
  success: {
    fill: 'var(--a-ok)',
    hover: 'var(--a-ok-hover)',
    text: 'var(--a-ok)',
    soft: 'var(--a-ok-soft)',
    on: 'var(--a-on-tone)',
  },
  warning: {
    fill: 'var(--a-warn)',
    hover: 'var(--a-warn-hover)',
    text: 'var(--a-warn)',
    soft: 'var(--a-warn-soft)',
    on: 'var(--a-on-tone)',
  },
  info: {
    fill: 'var(--a-info)',
    hover: 'var(--a-info-hover)',
    text: 'var(--a-info)',
    soft: 'var(--a-info-soft)',
    on: 'var(--a-on-tone)',
  },
};

/**
 * Button/ActionIcon/Badge colours. Primary `subtle` buttons use the text colour (handoff:
 * "Subtle" is ink on transparent), `light`/`outline`/`transparent` use the accent-text token,
 * and the semantic colours follow the scheme. Shaded colours (`danger.7`) and other palettes
 * keep Mantine's defaults.
 */
const variantColorResolver: VariantColorsResolver = (input) => {
  const base = defaultVariantColorsResolver(input);
  const tone = TONES[input.color ?? input.theme.primaryColor];
  if (!tone) return base;
  const isPrimary = (input.color ?? input.theme.primaryColor) === input.theme.primaryColor;
  const border = (c: string) => `${rem(1)} solid ${c}`;
  switch (input.variant) {
    case 'filled':
      return {
        background: tone.fill,
        hover: tone.hover,
        color: tone.on,
        border: border('transparent'),
      };
    case 'light':
      return {
        background: tone.soft,
        hover: tone.soft,
        color: tone.text,
        border: border('transparent'),
      };
    case 'outline':
      return {
        background: 'transparent',
        hover: tone.soft,
        color: tone.text,
        border: border(tone.text),
      };
    case 'subtle':
      return {
        background: 'transparent',
        hover: isPrimary ? 'var(--a-surface-2)' : tone.soft,
        color: isPrimary ? 'var(--mantine-color-text)' : tone.text,
        border: border('transparent'),
      };
    case 'transparent':
      return {
        background: 'transparent',
        hover: 'transparent',
        color: tone.text,
        border: border('transparent'),
      };
    default:
      return base;
  }
};

export const theme = createTheme({
  primaryColor: 'spartan',
  // Filled primary is brand yellow in both schemes (black text). Non-filled uses are routed
  // through `variantColorResolver` and the `--mantine-color-spartan-*` overrides below, so
  // yellow never ends up as text on white.
  primaryShade: { light: 5, dark: 5 },
  variantColorResolver,
  autoContrast: true, // black text on yellow
  luminanceThreshold: 0.45,
  colors: { spartan, dark, success, warning, danger, info },
  black: '#0A0A0B',
  white: '#FFFFFF',
  fontFamily: 'Archivo, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  fontFamilyMonospace: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
  headings: {
    fontFamily: '"Barlow Condensed", Archivo, system-ui, sans-serif',
    fontWeight: '800',
    sizes: {
      h1: { fontSize: rem(48), lineHeight: '1.05' },
      h2: { fontSize: rem(32), lineHeight: '1.1' },
      h3: { fontSize: rem(24), lineHeight: '1.15' },
    },
  },
  spacing: { xs: rem(4), sm: rem(8), md: rem(12), lg: rem(16), xl: rem(24) },
  radius: { xs: rem(2), sm: rem(4), md: rem(8), lg: rem(12), xl: rem(999) },
  defaultRadius: 'sm',
  shadows: { sm: '0 1px 3px rgba(0,0,0,.18)', md: '0 8px 24px rgba(0,0,0,.5)' },
  focusRing: 'auto',
  components: {
    // One yellow (filled) button per screen: everything defaults to the bordered "Default"
    // style and the screen's primary action opts in with variant="filled".
    Button: { defaultProps: { size: 'sm', variant: 'default' } },
    Title: { styles: { root: { fontStyle: 'italic', textTransform: 'uppercase' } } },
    Table: { defaultProps: { highlightOnHover: true, verticalSpacing: 'sm' } },
    Badge: { defaultProps: { variant: 'light', radius: 'xl' } },
    Card: { defaultProps: { withBorder: true, radius: 'md', padding: 'lg' } },
    Notification: { defaultProps: { withBorder: true } },
    Skeleton: { defaultProps: { animate: true } },
  },
  other: {
    motion: { fast: 120, base: 200, slow: 320, hero: 600, ease: 'cubic-bezier(.2,.8,.2,1)' },
    chart: ['#FFD400', 'var(--mantine-color-text)', '#2BB5C6', '#FF9F43', '#9D8CFF', '#3DD68C'],
  },
});

/** Mantine's per-colour variables for a semantic colour in light scheme (shade 7 for text). */
function semanticLight(name: string, base: string, hover: string, soft: string) {
  return {
    [`--mantine-color-${name}-text`]: base,
    [`--mantine-color-${name}-filled`]: base,
    [`--mantine-color-${name}-filled-hover`]: hover,
    [`--mantine-color-${name}-light`]: soft,
    [`--mantine-color-${name}-light-hover`]: soft,
    [`--mantine-color-${name}-light-color`]: base,
    [`--mantine-color-${name}-outline`]: base,
    [`--mantine-color-${name}-outline-hover`]: soft,
  };
}

/** Surface/border/text and semantic (ok/warn/danger/info + soft fills) tokens per scheme, as `--a-*` CSS variables. */
export const cssVariablesResolver: CSSVariablesResolver = () => ({
  variables: {
    '--a-shell-bg': '#0A0A0B', // top bar stays black in both schemes
    '--a-accent': '#FFD400',
  },
  dark: {
    '--a-body': '#0A0A0B',
    '--a-surface': '#141416',
    '--a-surface-2': '#1D1D20',
    '--a-border': '#2C2C30',
    '--a-text': '#F5F5F2',
    '--a-text-muted': '#A3A3A8',
    '--a-accent-text': '#FFD400',
    '--a-accent-soft': 'rgba(255,212,0,.12)',
    '--a-ok': '#3DD68C',
    '--a-ok-soft': 'rgba(61,214,140,.12)',
    '--a-warn': '#FF9F43',
    '--a-warn-soft': 'rgba(255,159,67,.14)',
    '--a-danger': '#FF6B6B',
    '--a-danger-soft': 'rgba(255,107,107,.13)',
    '--a-on-danger': '#0A0A0B',
    '--a-info': '#6CB2FF',
    '--a-info-soft': 'rgba(108,178,255,.13)',
    '--a-shadow': '0 8px 24px rgba(0,0,0,.5)',
    '--a-focus': '#FFD400',
    '--a-on-tone': '#0A0A0B',
    '--a-danger-hover': '#FF8D8D',
    '--a-ok-hover': '#6EDFA8',
    '--a-warn-hover': '#FFB56B',
    '--a-info-hover': '#86BBFF',
    '--mantine-color-anchor': '#FFD400',
    '--mantine-color-default': 'var(--a-surface)',
    '--mantine-color-default-hover': 'var(--a-surface-2)',
    '--mantine-color-default-color': 'var(--a-text)',
    '--mantine-color-default-border': '#2C2C30',
    '--mantine-color-body': '#0A0A0B',
    '--mantine-color-text': '#F5F5F2',
    '--mantine-color-dimmed': '#A3A3A8',
    // Disabled buttons and checkboxes: readable on the dark surface (the audit found the
    // disabled "Send request" and half-day box nearly invisible).
    '--mantine-color-disabled': '#26262A',
    '--mantine-color-disabled-color': '#8C8C92',
    '--mantine-color-disabled-border': '#3A3A3F',
  },
  light: {
    '--a-body': '#F5F5F2',
    '--a-surface': '#FFFFFF',
    '--a-surface-2': '#EEEEEA',
    '--a-border': '#DEDED9',
    '--a-text': '#121214',
    '--a-text-muted': '#5C5C62',
    '--a-accent-text': '#6B5600', // yellow is never text on white
    '--a-accent-soft': '#FFF6C2',
    '--a-ok': '#16803F',
    '--a-ok-soft': '#E3F5EA',
    '--a-warn': '#B45309',
    '--a-warn-soft': '#FDEEDC',
    '--a-danger': '#C62828',
    '--a-danger-soft': '#FCE8E8',
    '--a-on-danger': '#FFFFFF',
    '--a-info': '#1D5FC4',
    '--a-info-soft': '#E5EEFB',
    '--a-shadow': '0 8px 24px rgba(18,18,20,.12)',
    // Focus ring: ink on light surfaces (yellow on white is ~1.4:1).
    '--a-focus': '#121214',
    '--a-on-tone': '#FFFFFF',
    '--a-danger-hover': '#9A1E1E',
    '--a-ok-hover': '#0F5E2E',
    '--a-warn-hover': '#8A3F06',
    '--a-info-hover': '#164A99',
    '--mantine-color-anchor': '#6B5600',
    '--mantine-color-default': 'var(--a-surface)',
    '--mantine-color-default-hover': 'var(--a-surface-2)',
    '--mantine-color-default-color': 'var(--a-text)',
    '--mantine-color-disabled': '#EEEEEA',
    '--mantine-color-disabled-color': '#7A7A80',
    '--mantine-color-disabled-border': '#D2D2CC',
    // Primary as text, outline or soft fill: never yellow on white.
    '--mantine-color-spartan-text': '#6B5600',
    '--mantine-color-spartan-light': '#FFF6C2',
    '--mantine-color-spartan-light-hover': '#FFEC80',
    '--mantine-color-spartan-light-color': '#6B5600',
    '--mantine-color-spartan-outline': '#6B5600',
    '--mantine-color-spartan-outline-hover': '#FFF6C2',
    // Semantic colours as text in light use shade 7 (danger #C62828, not #FF6B6B).
    ...semanticLight('danger', '#C62828', '#9A1E1E', '#FCE8E8'),
    ...semanticLight('success', '#16803F', '#0F5E2E', '#E3F5EA'),
    ...semanticLight('warning', '#B45309', '#8A3F06', '#FDEEDC'),
    ...semanticLight('info', '#1D5FC4', '#164A99', '#E5EEFB'),
    '--mantine-color-body': '#F5F5F2',
    '--mantine-color-text': '#121214',
    '--mantine-color-dimmed': '#5C5C62',
    '--mantine-color-default-border': '#DEDED9',
  },
});
